import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../../App";
import { applyTheme } from "../ThemeProvider";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Save, RotateCcw, History, Palette, Code, AlertTriangle, Check, Image as ImageIcon, Upload, X } from "lucide-react";
import { extractApiError } from "../../lib/apiError";

const COLOR_FIELDS = [
  { key: "color_accent", label: "Accent (Brand)" },
  { key: "color_accent_hover", label: "Accent Hover" },
  { key: "color_bg", label: "Background" },
  { key: "color_bg_card", label: "Card Background" },
  { key: "color_bg_elevated", label: "Elevated Surface" },
  { key: "color_text", label: "Text" },
  { key: "color_hero_overlay_color", label: "Hero Overlay" }
];

const FONT_OPTIONS = [
  "'Playfair Display', serif",
  "'Cormorant Garamond', serif",
  "'Cinzel', serif",
  "'Manrope', sans-serif",
  "'Inter', sans-serif",
  "Georgia, serif",
  "system-ui, sans-serif"
];

const RADIUS_OPTIONS = [
  { v: "0px", l: "Sharp" },
  { v: "2px", l: "Subtle" },
  { v: "8px", l: "Rounded" },
  { v: "999px", l: "Pill" }
];

export default function ThemeTab() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [preset, setPreset] = useState();
  const [customCss, setCustomCss] = useState("");
  const [history, setHistory] = useState([]);
  const [defaults, setDefaults] = useState();
  const [activeSection, setActiveSection] = useState("colors");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/theme`);
      setPreset({ ...res.data.defaults.preset, ...res.data.active.preset });
      setCustomCss(res.data.active.custom_css || "");
      setHistory(res.data.history || []);
      setDefaults(res.data.defaults);
    } catch (_e) {
      toast.error("Failed to load theme");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const setField = (key, value) => setPreset(p => ({ ...p, [key]: value }));

  const save = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/admin/theme`, { preset, custom_css: customCss });
      applyTheme(res.data);
      window.dispatchEvent(new CustomEvent("theme:update", { detail: res.data }));
      toast.success("Theme saved & applied");
      load();
    } catch (e) {
      toast.error(extractApiError(e, "Failed to save"));
    } finally { setSaving(false); }
  };

  const reset = async () => {
    if (!window.confirm("Reset theme to defaults? Current settings will be backed up to history.")) return;
    try {
      const res = await axios.post(`${API}/admin/theme/reset`);
      applyTheme(res.data);
      window.dispatchEvent(new CustomEvent("theme:update", { detail: res.data }));
      toast.success("Theme reset to default");
      load();
    } catch (_e) { toast.error("Failed to reset"); }
  };

  const rollback = async (versionId) => {
    if (!window.confirm("Roll back to this version? Current settings will be backed up.")) return;
    try {
      const res = await axios.post(`${API}/admin/theme/rollback/${versionId}`);
      applyTheme(res.data);
      window.dispatchEvent(new CustomEvent("theme:update", { detail: res.data }));
      toast.success("Rolled back successfully");
      load();
    } catch (_e) { toast.error("Failed to rollback"); }
  };

  if (loading) return <div className="flex justify-center py-20"><div className="spinner" /></div>;

  return (
    <div data-testid="theme-tab" className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-xl font-semibold text-white flex items-center gap-2">
            <Palette className="w-5 h-5 text-[#C5A059]" /> Theme & CSS Customization
          </h2>
          <p className="text-white/40 text-xs mt-1">Changes apply immediately to the entire site (public + admin).</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={reset} variant="ghost" className="text-white/60 hover:text-red-400" data-testid="reset-theme-btn">
            <RotateCcw className="w-4 h-4 mr-2" /> Reset to Default
          </Button>
          <Button onClick={save} disabled={saving} className="btn-gold" data-testid="save-theme-btn">
            <Save className="w-4 h-4 mr-2" /> {saving ? "Saving..." : "Save & Apply"}
          </Button>
        </div>
      </div>

      <div className="flex gap-2 border-b border-white/5">
        {[
          { id: "colors", label: "Colors", icon: Palette },
          { id: "branding", label: "Branding", icon: ImageIcon },
          { id: "typography", label: "Typography", icon: Code },
          { id: "texts", label: "Site Texts", icon: Code },
          { id: "advanced", label: "Advanced CSS", icon: Code },
          { id: "history", label: `History (${history.length})`, icon: History }
        ].map(s => (
          <button key={s.id} onClick={() => setActiveSection(s.id)}
            className={`flex items-center gap-2 px-4 py-3 text-sm border-b-2 transition-colors ${activeSection === s.id ? "border-[#C5A059] text-[#C5A059]" : "border-transparent text-white/50 hover:text-white"}`}
            data-testid={`theme-section-${s.id}`}>
            <s.icon className="w-4 h-4" /> {s.label}
          </button>
        ))}
      </div>

      {activeSection === "colors" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {COLOR_FIELDS.map(f => {
            const fieldKey = f.key === "color_hero_overlay_color" ? "hero_overlay_color" : f.key;
            return (
              <div key={f.key} className="bg-[#151515] border border-white/5 rounded-sm p-4">
                <label className="text-white/60 text-xs uppercase mb-2 block">{f.label}</label>
                <div className="flex items-center gap-3">
                  <input type="color" value={preset[fieldKey] || "#000000"}
                    onChange={(e) => setField(fieldKey, e.target.value)}
                    className="w-12 h-10 rounded-sm border border-white/10 bg-transparent cursor-pointer"
                    data-testid={`color-${fieldKey}`} />
                  <Input value={preset[fieldKey] || ""} onChange={(e) => setField(fieldKey, e.target.value)}
                    className="input-dark font-mono text-xs flex-1" placeholder="#FFFFFF or rgba(...)" />
                </div>
              </div>
            );
          })}
          <div className="bg-[#151515] border border-white/5 rounded-sm p-4">
            <label className="text-white/60 text-xs uppercase mb-2 block">Hero Overlay Opacity</label>
            <input type="range" min="0" max="1" step="0.05"
              value={preset.hero_overlay_opacity || "0.55"}
              onChange={(e) => setField("hero_overlay_opacity", e.target.value)}
              className="w-full accent-[#C5A059]" />
            <p className="text-white/40 text-xs mt-2 font-mono">{preset.hero_overlay_opacity}</p>
          </div>
        </div>
      )}

      {activeSection === "branding" && (
        <BrandingSection
          faviconUrl={preset.favicon_url || ""}
          galleryRaw={preset.hero_background_gallery || ""}
          onChangeFavicon={(v) => setField("favicon_url", v)}
          onChangeGallery={(v) => setField("hero_background_gallery", v)}
          fleetImages={{
            v1: preset.fleet_v1_image_url || "",
            v2: preset.fleet_v2_image_url || "",
            v3: preset.fleet_v3_image_url || "",
          }}
          onChangeFleetImage={(slot, v) => setField(`fleet_${slot}_image_url`, v)}
        />
      )}

      {activeSection === "typography" && (
        <div className="space-y-4">
          {[
            { key: "font_family_heading", label: "Heading Font" },
            { key: "font_family_body", label: "Body Font" },
            { key: "font_family_mono", label: "Mono Font" }
          ].map(f => (
            <div key={f.key} className="bg-[#151515] border border-white/5 rounded-sm p-4">
              <label className="text-white/60 text-xs uppercase mb-2 block">{f.label}</label>
              <div className="grid grid-cols-2 gap-3">
                <Input value={preset[f.key] || ""} onChange={(e) => setField(f.key, e.target.value)}
                  className="input-dark font-mono text-xs" />
                <select value={FONT_OPTIONS.includes(preset[f.key]) ? preset[f.key] : ""}
                  onChange={(e) => e.target.value && setField(f.key, e.target.value)}
                  className="input-dark text-xs">
                  <option value="">— Quick pick —</option>
                  {FONT_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
              <p className="mt-3 text-2xl text-white" style={{ fontFamily: preset[f.key] }}>The quick brown fox jumps</p>
            </div>
          ))}

          <div className="bg-[#151515] border border-white/5 rounded-sm p-4">
            <label className="text-white/60 text-xs uppercase mb-2 block">Logo Text</label>
            <Input value={preset.logo_text || ""} onChange={(e) => setField("logo_text", e.target.value)}
              className="input-dark" maxLength={32} data-testid="logo-text" />
          </div>

          <div className="bg-[#151515] border border-white/5 rounded-sm p-4">
            <label className="text-white/60 text-xs uppercase mb-3 block">Button Radius</label>
            <div className="flex gap-2 flex-wrap">
              {RADIUS_OPTIONS.map(r => (
                <button key={r.v} type="button" onClick={() => setField("button_radius", r.v)}
                  className={`px-4 py-2 text-xs border transition-colors ${preset.button_radius === r.v ? "bg-[#C5A059]/20 text-[#C5A059] border-[#C5A059]/30" : "bg-white/5 text-white/60 border-white/10 hover:bg-white/10"}`}
                  style={{ borderRadius: r.v }}
                  data-testid={`radius-${r.l.toLowerCase()}`}>
                  {r.l}
                </button>
              ))}
              <Input value={preset.button_radius || ""} onChange={(e) => setField("button_radius", e.target.value)}
                className="input-dark w-32 text-xs" placeholder="Custom (e.g. 12px)" />
            </div>
          </div>
        </div>
      )}

      {activeSection === "texts" && (
        <div className="space-y-4">
          <p className="text-white/40 text-xs">
            These texts are shown across the public site. Changes apply immediately after Save.
          </p>
          {[
            { key: "site_html_title", label: "HTML <title> (browser tab)", multiline: false, max: 120 },
            { key: "hero_h1_line1", label: "Hero H1 - Line 1", multiline: false, max: 60 },
            { key: "hero_h1_line2", label: "Hero H1 - Line 2 (gold accent)", multiline: false, max: 60 },
            { key: "hero_subtitle", label: "Hero Subtitle", multiline: true, max: 400 },
            { key: "hero_check_1", label: "Hero Check 1", multiline: false, max: 50 },
            { key: "hero_check_2", label: "Hero Check 2", multiline: false, max: 50 },
            { key: "hero_check_3", label: "Hero Check 3", multiline: false, max: 50 },
            { key: "booking_cta_primary", label: "Booking CTA (button label)", multiline: false, max: 40 },
            { key: "booking_cta_subtitle", label: "Booking CTA (sub-label)", multiline: false, max: 80 },
            // Fleet section
            { key: "fleet_section_title", label: "Fleet section - Title", multiline: false, max: 120 },
            { key: "fleet_section_subtitle", label: "Fleet section - Subtitle", multiline: true, max: 300 },
            { key: "fleet_v1_name", label: "Fleet · Vehicle 1 · Name", multiline: false, max: 60 },
            { key: "fleet_v1_desc", label: "Fleet · Vehicle 1 · Description", multiline: true, max: 200 },
            { key: "fleet_v1_pax", label: "Fleet · Vehicle 1 · Passengers (label)", multiline: false, max: 12 },
            { key: "fleet_v1_luggage", label: "Fleet · Vehicle 1 · Luggage (label)", multiline: false, max: 12 },
            { key: "fleet_v1_rate", label: "Fleet · Vehicle 1 · Rate badge", multiline: false, max: 40 },
            { key: "fleet_v2_name", label: "Fleet · Vehicle 2 · Name", multiline: false, max: 60 },
            { key: "fleet_v2_desc", label: "Fleet · Vehicle 2 · Description", multiline: true, max: 200 },
            { key: "fleet_v2_pax", label: "Fleet · Vehicle 2 · Passengers (label)", multiline: false, max: 12 },
            { key: "fleet_v2_luggage", label: "Fleet · Vehicle 2 · Luggage (label)", multiline: false, max: 12 },
            { key: "fleet_v2_rate", label: "Fleet · Vehicle 2 · Rate badge", multiline: false, max: 40 },
            { key: "fleet_v3_name", label: "Fleet · Vehicle 3 · Name", multiline: false, max: 60 },
            { key: "fleet_v3_desc", label: "Fleet · Vehicle 3 · Description", multiline: true, max: 200 },
            { key: "fleet_v3_pax", label: "Fleet · Vehicle 3 · Passengers (label)", multiline: false, max: 12 },
            { key: "fleet_v3_luggage", label: "Fleet · Vehicle 3 · Luggage (label)", multiline: false, max: 12 },
            { key: "fleet_v3_rate", label: "Fleet · Vehicle 3 · Rate badge", multiline: false, max: 40 },
            { key: "prefooter_title", label: "Pre-footer Title", multiline: false, max: 120 },
            { key: "prefooter_subtitle", label: "Pre-footer Subtitle", multiline: true, max: 300 },
            { key: "footer_about", label: "Footer About Text", multiline: true, max: 300 },
            { key: "footer_contact_phone", label: "Footer - Phone", multiline: false, max: 50 },
            { key: "footer_contact_email", label: "Footer - Email", multiline: false, max: 80 },
            { key: "footer_copyright", label: "Footer - Copyright Line", multiline: false, max: 200 }
          ].map(f => (
            <div key={f.key} className="bg-[#151515] border border-white/5 rounded-sm p-4">
              <label className="text-white/60 text-xs uppercase mb-2 block">{f.label}</label>
              {f.multiline ? (
                <textarea
                  value={preset[f.key] ?? ""}
                  onChange={(e) => setField(f.key, e.target.value)}
                  maxLength={f.max}
                  rows={3}
                  className="w-full bg-[#0A0A0A] border border-[#333] rounded-sm p-2 text-white/90 text-sm resize-y focus:border-[#C5A059]/50 focus:outline-none"
                  data-testid={`text-${f.key}`}
                />
              ) : (
                <Input
                  value={preset[f.key] ?? ""}
                  onChange={(e) => setField(f.key, e.target.value)}
                  maxLength={f.max}
                  className="input-dark"
                  data-testid={`text-${f.key}`}
                />
              )}
              <p className="text-white/30 text-[10px] mt-1 text-right">{(preset[f.key] || "").length} / {f.max}</p>
            </div>
          ))}
        </div>
      )}

      {activeSection === "advanced" && (
        <div className="space-y-4">
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-sm p-4 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
            <div className="text-amber-300/90 text-sm">
              <p className="font-semibold">Advanced CSS — applied site-wide</p>
              <p className="text-amber-300/70 text-xs mt-1">
                Server-side sanitization is applied automatically. The following are blocked:
                <code className="ml-1 text-amber-200">@import</code>,
                <code className="ml-1 text-amber-200">javascript:</code>,
                <code className="ml-1 text-amber-200">expression()</code>,
                <code className="ml-1 text-amber-200">behavior:</code>,
                <code className="ml-1 text-amber-200">-moz-binding</code>,
                non-image data URLs, HTML tags. Max 50&nbsp;KB.
              </p>
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-white/60 text-xs uppercase">Custom CSS</label>
              <span className="text-white/30 text-xs">{customCss.length} / 50000</span>
            </div>
            <textarea
              value={customCss}
              onChange={(e) => setCustomCss(e.target.value)}
              className="w-full h-96 bg-[#0A0A0A] border border-[#333] rounded-sm p-4 text-emerald-300 text-xs font-mono resize-y focus:border-[#C5A059]/50 focus:outline-none"
              placeholder={`/* Example */\n.btn-gold {\n  box-shadow: 0 4px 20px rgba(197,160,89,0.3);\n}\n\n.hero-section h1 {\n  text-shadow: 2px 2px 8px rgba(0,0,0,0.8);\n}`}
              spellCheck="false"
              data-testid="custom-css"
            />
          </div>

          <div className="bg-[#151515] border border-white/5 rounded-sm p-4">
            <p className="text-white/60 text-xs uppercase mb-2 flex items-center gap-2">
              <Check className="w-3.5 h-3.5 text-emerald-400" /> Allowed CSS at-rules
            </p>
            <p className="text-white/40 text-xs">@media, @keyframes, @supports, @font-face, @container — all standard selectors and properties.</p>
          </div>
        </div>
      )}

      {activeSection === "history" && (
        <div className="space-y-3">
          {history.length === 0 ? (
            <p className="text-white/40 text-center py-10">No previous versions yet. Save changes to start building history.</p>
          ) : history.map(v => (
            <div key={v.version_id} className="bg-[#151515] border border-white/5 rounded-sm p-4 flex items-center justify-between gap-4" data-testid={`history-${v.version_id}`}>
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm font-mono">{v.version_id}</p>
                <p className="text-white/40 text-xs mt-1">
                  {v.created_at?.replace("T", " ").slice(0, 19)} &middot; by {v.created_by}
                </p>
                <div className="flex items-center gap-2 mt-2">
                  {Object.entries(v.preset || {}).slice(0, 5).map(([k, val]) => {
                    if (!k.startsWith("color_")) return null;
                    return <span key={k} className="w-5 h-5 rounded-sm border border-white/10" style={{ background: val }} title={`${k}: ${val}`} />;
                  })}
                  {v.custom_css && <span className="text-violet-400 text-[10px] bg-violet-500/10 px-2 py-0.5 rounded">+ Custom CSS</span>}
                </div>
              </div>
              <Button onClick={() => rollback(v.version_id)} className="bg-violet-600 hover:bg-violet-700 text-white" size="sm"
                data-testid={`rollback-${v.version_id}`}>
                <RotateCcw className="w-3.5 h-3.5 mr-1" /> Rollback
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function BrandingSection({ faviconUrl, galleryRaw, onChangeFavicon, onChangeGallery, fleetImages, onChangeFleetImage }) {
  const [uploading, setUploading] = useState(false);
  const [galleryUploading, setGalleryUploading] = useState(false);
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const previewSrc = faviconUrl
    ? (faviconUrl.startsWith("/api/") ? `${BACKEND_URL}${faviconUrl}` : faviconUrl)
    : "/favicon.ico";

  const galleryUrls = (galleryRaw || "")
    .split(/[\r\n]+/)
    .map((u) => u.trim())
    .filter(Boolean);

  const galleryResolve = (u) => (u.startsWith("/api/") ? `${BACKEND_URL}${u}` : u);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File too large (max 5 MB)");
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      const res = await axios.post(`${API}/admin/uploads/image`, fd, { headers: { "Content-Type": "multipart/form-data" },
      });
      onChangeFavicon(res.data.url);
      toast.success("Favicon uploaded — click Save & Apply to publish");
    } catch (err) {
      toast.error(extractApiError(err, "Upload failed"));
    } finally {
      setUploading(false);
    }
  };

  const handleGalleryUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (files.length === 0) return;
    const oversized = files.find((f) => f.size > 5 * 1024 * 1024);
    if (oversized) {
      toast.error(`"${oversized.name}" is larger than 5 MB`);
      return;
    }
    setGalleryUploading(true);
    const newlyAdded = [];
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const res = await axios.post(`${API}/admin/uploads/image`, fd, { headers: { "Content-Type": "multipart/form-data" },
        });
        newlyAdded.push(res.data.url);
      }
      const combined = [...galleryUrls, ...newlyAdded].join("\n");
      onChangeGallery(combined);
      toast.success(`${newlyAdded.length} image${newlyAdded.length > 1 ? "s" : ""} added — click Save & Apply to publish`);
    } catch (err) {
      toast.error(extractApiError(err, "Upload failed"));
    } finally {
      setGalleryUploading(false);
    }
  };

  const removeGalleryItem = (idx) => {
    const next = galleryUrls.filter((_, i) => i !== idx);
    onChangeGallery(next.join("\n"));
  };

  return (
    <div className="space-y-6" data-testid="branding-section">
      {/* Favicon */}
      <div className="bg-[#151515] border border-white/5 rounded-sm p-5">
        <label className="text-white/60 text-xs uppercase mb-3 block flex items-center gap-2">
          <ImageIcon className="w-3.5 h-3.5 text-[#C5A059]" /> Browser Tab Icon (Favicon)
        </label>
        <p className="text-white/40 text-xs mb-4">
          Upload a small square image (PNG, ICO, SVG or WEBP recommended — 32×32 or 64×64 pixels works best).
          The image shows up in the browser tab beside the page title.
        </p>

        <div className="flex items-center gap-5 mb-4">
          <div
            className="w-16 h-16 rounded-sm border border-white/10 bg-[#0A0A0A] flex items-center justify-center overflow-hidden flex-shrink-0"
            data-testid="favicon-preview"
          >
            <img
              src={previewSrc}
              alt="Favicon preview"
              className="max-w-full max-h-full"
              onError={(e) => {
                e.currentTarget.src = "/favicon.ico";
              }}
            />
          </div>
          <div className="flex flex-col gap-2">
            <label
              className={`btn-gold inline-flex items-center gap-2 px-4 py-2 text-xs cursor-pointer rounded-sm w-fit ${uploading ? "opacity-60 pointer-events-none" : ""}`}
              data-testid="favicon-upload-btn"
            >
              <Upload className="w-3.5 h-3.5" />
              {uploading ? "Uploading..." : "Upload favicon"}
              <input
                type="file"
                accept="image/png,image/x-icon,image/vnd.microsoft.icon,image/svg+xml,image/webp,.ico,.png,.svg,.webp"
                onChange={handleFile}
                className="hidden"
                disabled={uploading}
              />
            </label>
            {faviconUrl && (
              <button
                type="button"
                onClick={() => onChangeFavicon("")}
                className="text-red-400/80 hover:text-red-400 text-xs flex items-center gap-1 w-fit"
                data-testid="favicon-remove-btn"
              >
                <X className="w-3 h-3" /> Reset to default
              </button>
            )}
          </div>
        </div>

        <label className="text-white/50 text-[10px] uppercase mb-1 block">Favicon URL</label>
        <Input
          value={faviconUrl}
          onChange={(e) => onChangeFavicon(e.target.value)}
          placeholder="/api/files/... or https://..."
          className="input-dark font-mono text-xs"
          data-testid="favicon-url-input"
        />
        <p className="text-white/30 text-[10px] mt-2">
          Click "Save &amp; Apply" at the top to publish the new favicon across the site.
        </p>
      </div>

      {/* Hero rotating gallery */}
      <div className="bg-[#151515] border border-white/5 rounded-sm p-5" data-testid="hero-gallery-section">
        <label className="text-white/60 text-xs uppercase mb-3 block flex items-center gap-2">
          <ImageIcon className="w-3.5 h-3.5 text-[#C5A059]" /> Homepage Hero Background (rotating gallery)
        </label>
        <p className="text-white/40 text-xs mb-4">
          Upload one or more wide images (recommended 1920×1080+). The homepage hero will cycle through them with a soft fade every 6 seconds. Leave empty to use the built-in default.
        </p>

        <div className="flex items-center gap-3 mb-4">
          <label
            className={`btn-gold inline-flex items-center gap-2 px-4 py-2 text-xs cursor-pointer rounded-sm w-fit ${galleryUploading ? "opacity-60 pointer-events-none" : ""}`}
            data-testid="hero-gallery-upload-btn"
          >
            <Upload className="w-3.5 h-3.5" />
            {galleryUploading ? "Uploading..." : "Add images to gallery"}
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp"
              multiple
              onChange={handleGalleryUpload}
              className="hidden"
              disabled={galleryUploading}
            />
          </label>
          {galleryUrls.length > 0 && (
            <button
              type="button"
              onClick={() => onChangeGallery("")}
              className="text-red-400/80 hover:text-red-400 text-xs flex items-center gap-1"
              data-testid="hero-gallery-clear-btn"
            >
              <X className="w-3 h-3" /> Clear all
            </button>
          )}
        </div>

        {galleryUrls.length === 0 ? (
          <p className="text-white/30 text-xs italic">No custom images yet — using the default hero background.</p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="hero-gallery-grid">
            {galleryUrls.map((u, i) => (
              <div key={u + i} className="relative group bg-[#0A0A0A] border border-white/10 rounded-sm overflow-hidden aspect-video" data-testid={`hero-gallery-item-${i}`}>
                <img src={galleryResolve(u)} alt={`hero ${i + 1}`} className="w-full h-full object-cover" />
                <button
                  type="button"
                  onClick={() => removeGalleryItem(i)}
                  className="absolute top-1 right-1 bg-black/70 hover:bg-red-500/80 text-white rounded-sm p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                  data-testid={`hero-gallery-remove-${i}`}
                  title="Remove"
                >
                  <X className="w-3 h-3" />
                </button>
                <span className="absolute bottom-1 left-1 bg-black/70 text-white/80 text-[10px] px-1.5 py-0.5 rounded-sm">{i + 1}</span>
              </div>
            ))}
          </div>
        )}

        <p className="text-white/30 text-[10px] mt-3">Click "Save &amp; Apply" at the top to publish gallery changes.</p>
      </div>

      {/* Fleet images (homepage Fleet section) */}
      <div className="bg-[#151515] border border-white/5 rounded-sm p-5" data-testid="fleet-images-section">
        <label className="text-white/60 text-xs uppercase mb-3 block flex items-center gap-2">
          <ImageIcon className="w-3.5 h-3.5 text-[#C5A059]" /> Homepage Fleet Section — Vehicle Images
        </label>
        <p className="text-white/40 text-xs mb-4">
          Each card on the homepage Fleet section can have its own picture. Recommended 1600×900 (16:9). Leave empty to use the built-in default.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            { slot: "v1", label: "Vehicle 1 (V-Class)" },
            { slot: "v2", label: "Vehicle 2 (S-Class)" },
            { slot: "v3", label: "Vehicle 3 (Sprinter)" },
          ].map((f) => (
            <FleetImageUpload
              key={f.slot}
              slot={f.slot}
              label={f.label}
              url={fleetImages?.[f.slot] || ""}
              onChange={(v) => onChangeFleetImage(f.slot, v)}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function FleetImageUpload({ slot, label, url, onChange }) {
  const [uploading, setUploading] = useState(false);
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const previewSrc = url ? (url.startsWith("/api/") ? `${BACKEND_URL}${url}` : url) : null;

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("File too large (max 5 MB)"); return; }
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      const res = await axios.post(`${API}/admin/uploads/image`, fd, { headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(res.data.url);
      toast.success(`${label} image uploaded — click Save & Apply`);
    } catch (err) {
      toast.error(extractApiError(err, "Upload failed"));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="bg-[#0F0F0F] border border-white/5 rounded-sm p-3" data-testid={`fleet-${slot}-image-block`}>
      <p className="text-white/70 text-xs font-medium mb-2">{label}</p>
      <div className="aspect-video w-full bg-[#0A0A0A] border border-white/10 rounded-sm overflow-hidden mb-2 flex items-center justify-center">
        {previewSrc ? (
          <img src={previewSrc} alt={label} className="w-full h-full object-cover" data-testid={`fleet-${slot}-preview`} />
        ) : (
          <ImageIcon className="w-6 h-6 text-white/20" />
        )}
      </div>
      <label
        className={`btn-gold inline-flex items-center gap-2 px-3 py-1.5 text-[11px] cursor-pointer rounded-sm w-fit ${uploading ? "opacity-60 pointer-events-none" : ""}`}
        data-testid={`fleet-${slot}-upload-btn`}
      >
        <Upload className="w-3.5 h-3.5" />
        {uploading ? "Uploading..." : url ? "Replace" : "Upload"}
        <input
          type="file"
          accept="image/jpeg,image/png,image/webp"
          onChange={handleFile}
          className="hidden"
          disabled={uploading}
        />
      </label>
      {url && (
        <button
          type="button"
          onClick={() => onChange("")}
          className="ml-2 text-red-400/80 hover:text-red-400 text-[11px] inline-flex items-center gap-1"
          data-testid={`fleet-${slot}-remove-btn`}
        >
          <X className="w-3 h-3" /> Remove
        </button>
      )}
      <Input
        value={url}
        onChange={(e) => onChange(e.target.value)}
        placeholder="/api/files/... or https://..."
        className="input-dark font-mono text-[10px] mt-2"
        data-testid={`fleet-${slot}-url-input`}
      />
    </div>
  );
}
